import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { isRealChange } from '../../../core/helper/simple-changes-typed';
import { getMonthName } from '../../../core/helper/month';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { InvoiceDeliveryMethod } from '../../../invoice/model/const/enum';
import {
  DirectDebitStatus,
  InvoicePeriod,
  InvoicePeriodToString,
  LeaseAgreementCategory,
  LeaseAgreementStatus,
  PeriodStartMonth,
  PeriodToString,
} from '../../model/const/enums';
import { Contract } from '../../model/contract';
import { Payer } from '../../model/payer';
import { RentPeriod } from '../../model/rent-period';
import { Tenant } from '../../model/tenant';
import { ContactService } from '../../service/data/contact.service';
import { TenantService } from '../../service/data/tenant.service';
import { PropertyService } from '../../service/data/property.service';
import { LanguageService } from '../../../core/service/language.service';
import { CompanyService } from '../../service/data/company.service';

@Component({
    selector: 'app-contract-invoicing',
    templateUrl: './contract-invoicing.component.html',
    styleUrls: ['./contract-invoicing.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractInvoicingComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private _dataService: ContactService,
    private _propertyDataService: PropertyService,
    private _messageService: MessageService,
    private readonly _languageService: LanguageService,
    private _navigation: NavigationService,
    private _fcf: FormControlExFactory,
    private readonly _companyService: CompanyService,
    public tenantService: TenantService
  ) {}

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
    if (this._payerSelectSub) {
      this._payerSelectSub.unsubscribe();
      this._payerSelectSub = null;
    }
    if (this._payerInfoSub) {
      this._payerInfoSub.unsubscribe();
      this._payerInfoSub = null;
    }
    if (this._externalAccountsReceivableSettingsSub) {
      this._externalAccountsReceivableSettingsSub.unsubscribe();
      this._externalAccountsReceivableSettingsSub = null;
    }
  }

  @Input() dataContext: Contract;
  @Input() form: FormGroup;
  private _addressForm: FormGroup;
  @Input() set addressForm(value: AbstractControl) {
    this._addressForm = value as FormGroup;
  }
  get addressForm(): FormGroup {
    return this._addressForm;
  }
  @Input() category: LeaseAgreementCategory;
  @Input() tenantOne: Tenant;
  @Input() contractSpecificInvoiceAddress: boolean;
  @Input() company: string;

  private _subscription = new Subscription();
  private _firstChange = true;
  private _payerSelectSub: Subscription;
  private _payerInfoSub: Subscription;
  private _externalAccountsReceivableSettingsSub: Subscription;

  bankAccount: string = '';
  externalAccountsReceivable: boolean | null = null;

  get canCancelDirectDebit(): boolean {
    return this.form.contains('cancelDirectDebit');
  }
  get canRequestDirectDebit(): boolean {
    return this.form.contains('createDirectDebit');
  }
  get canResetRequestDate(): boolean {
    return this.form.contains('resetDirectDebitRequestDate');
  }

  get invoiceDeliveryHelpText(): string {
    switch (this.category) {
      case LeaseAgreementCategory.CooperativeHousing:
        return 'LEASE_AGREEMENT.HELP_TEXT.INVOICE_DELIVERY_METHOD_BRF';      
      default:
        return 'LEASE_AGREEMENT.HELP_TEXT.INVOICE_DELIVERY_METHOD';
    }
  }

  get invoicePayerHelpText(): string {
    switch (this.category) {
      case LeaseAgreementCategory.CooperativeHousing:
        return 'LEASE_AGREEMENT.HELP_TEXT.INVOICE_PAYER_BRF';
      default:
        return 'LEASE_AGREEMENT.HELP_TEXT.INVOICE_PAYER';
    }
  }

  get showDirectDebitControls(): boolean {
    return this.form.controls['invoicePayment'].value;
  }
  get showDirectDebitStatus(): boolean {
    const isRequesting = this.form.contains('createDirectDebit') && this.form.controls['createDirectDebit'].value;
    return !isRequesting && this.dataContext.directDebitStatus !== undefined;
  }
  get showDirectDebitTermination(): boolean {
    return !!this.dataContext.directDebitTerminationDate && this.dataContext.directDebitStatus === DirectDebitStatus.Terminated;
  }
  get showPayerInfo(): boolean {
    if (this.canRequestDirectDebit) {
      return this.form.controls['createDirectDebit'].value;
    }
    return this.dataContext.directDebitStatus !== undefined;
  }

  get deliveryMethodEmail(): boolean {
    return (
      this.form.controls['invoiceDeliveryMethod'].value === InvoiceDeliveryMethod.EmailWithPDF ||
      this.form.controls['invoiceDeliveryMethod'].value === InvoiceDeliveryMethod.EmailWithoutPDF
    );
  }

  get displayPayerEmail(): boolean {
    return this.deliveryMethodEmail && this.form.controls['useContractSpecificInvoicingEmail'].value === false;
  }

  get displayContractSpecificEmail(): boolean {
    return this.deliveryMethodEmail && this.form.controls['useContractSpecificInvoicingEmail'].value === true;
  }

  get displayYearlyDueDateControls(): boolean {
    return !this.invoicePeriodMonth;
  }

  get invoicePeriod(): number {
    return this.form.controls['invoicePeriod'].value;
  }

  get invoicePeriodYear(): boolean {
    return this.form.controls['invoicePeriod'].value === InvoicePeriod.Year;
  }

  get invoicePeriodHalfYear(): boolean {
    return this.form.controls['invoicePeriod'].value === InvoicePeriod.HalfYear;
  }

  get invoicePeriodTertrial(): boolean {
    return this.form.controls['invoicePeriod'].value === InvoicePeriod.Tertrial;
  }

  get invoicePeriodQuarter(): boolean {
    return this.form.controls['invoicePeriod'].value === InvoicePeriod.Quarter;
  }

  get invoicePeriodTwoMonths(): boolean {
    return this.form.controls['invoicePeriod'].value === InvoicePeriod.TwoMonths;
  }

  get invoicePeriodMonth(): boolean {
    return this.form.controls['invoicePeriod'].value === InvoicePeriod.Month;
  }

  invoicePeriods = [
    { name: InvoicePeriodToString(InvoicePeriod.Month), value: InvoicePeriod.Month },
    { name: InvoicePeriodToString(InvoicePeriod.TwoMonths), value: InvoicePeriod.TwoMonths },
    { name: InvoicePeriodToString(InvoicePeriod.Quarter), value: InvoicePeriod.Quarter },
    { name: InvoicePeriodToString(InvoicePeriod.Tertrial), value: InvoicePeriod.Tertrial },
    { name: InvoicePeriodToString(InvoicePeriod.HalfYear), value: InvoicePeriod.HalfYear },
    { name: InvoicePeriodToString(InvoicePeriod.Year), value: InvoicePeriod.Year },
  ];

  dueDateMonths: { name: string; value: number }[];
  yearlyDueDateDays: { name: string; value: number }[];
  periodsFrom: { name: string; value: number }[];
  rentPeriods: RentPeriod[];

  ngOnChanges(changes: SimpleChanges): void {
    if (isRealChange(changes['company'])) {
      this._loadExternalAccountsReceivableSetting(changes['company'].currentValue ?? null);
    }
    const tenantChange = changes['tenantOne'];
    if (isRealChange(tenantChange)) {
      this.updatePayer(tenantChange.currentValue?.id || null);
    }
  }

  private updatePayer(contactId: string, getPayerInfo: boolean = false): void {
    const payerControl = this.form.controls['payer'];
    if (this._payerInfoSub) {
      if (payerControl?.value === contactId) {
        return; // already fetching
      }
      this._payerInfoSub.unsubscribe();
      this._payerInfoSub = null;
    }

    if (payerControl && payerControl?.value !== contactId) {
      payerControl.setValue(contactId);
      getPayerInfo = true;
    }

    if (contactId && getPayerInfo) {
      this._payerInfoSub = this._dataService.getPaymentInformationForId(contactId).subscribe(payer => {
        this.bankAccount = payer.bankAccount;
        this._payerInfoSub = null;
      });
    }
  }

  ngOnInit() {
    this.form.addControl(
      'invoiceDeliveryMethod',
      this._fcf.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', this.dataContext.invoiceDeliveryMethod)
    );
    this.form.addControl('invoicePayment', this._fcf.create('LEASE_AGREEMENT.DIRECT_DEBIT', null));
    this.form.addControl('invoicePeriod', this._fcf.create('LEASE_AGREEMENT.INVOICE_PERIOD', this.dataContext.invoicePeriod));
    this.form.addControl(
      'periodFromStartMonth',
      this._fcf.create('DUE_PERIODS.YEARLY_DUE_DATE_MONTH', this.dataContext.periodFromStartMonth)
    );
    this.form.addControl('dueDateMonthOffset', this._fcf.create('DUE_PERIODS.DUE_DATE_MONTH_OFFSET', this.dataContext.dueDateMonthOffset));
    this.form.addControl('yearlyDueDateDay', this._fcf.create('DUE_PERIODS.YEARLY_DUE_DATE_DAY', this.dataContext.yearlyDueDateDay));
    this.form.addControl(
      'payer',
      this._fcf.create('LEASE_AGREEMENT.PAYER', this.dataContext.payer, null, null, this.invoicePayerHelpText)
    );
    this.form.addControl('payerNumber', this._fcf.create('LEASE_AGREEMENT.PAYER_NUMBER', ''));
    this.form.addControl('payerEmailInvoicing', this._fcf.create('COMMON.EMAIL_INVOICING', ''));
    this.form.addControl(
      'useContractSpecificInvoicingEmail',
      this._fcf.create('LEASE_AGREEMENT.CONTRACT_SPECIFIC_EMAIL_INVOICING', this.dataContext.useContractSpecificInvoicingEmail)
    );
    this.form.addControl('emailInvoicing', this._fcf.create('COMMON.EMAIL_INVOICING', this.dataContext.emailInvoicing));
    this.form.addControl('yearlyDueDateMonth', this._fcf.create('LEASE_AGREEMENT.PAYER_NUMBER', this.dataContext.yearlyDueDateMonth));
    this.form.addControl('labeling', this._fcf.createMaxLength('LEASE_AGREEMENT.LABELING', this.dataContext.labeling, 100));

    if (this.dataContext.payer && typeof this.dataContext.payer === 'string') {
      this.updatePayer(this.dataContext.payer, true);
    }

    this._loadExternalAccountsReceivableSetting();

    this.form.controls['invoicePayment'].valueChanges.subscribe(value => {
      if (this.externalAccountsReceivable === true) {
        this._applyExternalAccountsReceivableSetting(true);
        return;
      }

      this._applyDirectDebitControls(value);
    });

    this.form.controls['periodFromStartMonth'].setValue(this._getStartMonthFromDbValues());

    this.form.controls['invoicePeriod'].valueChanges.subscribe(value => {
      this._invoicePeriodChanging(value);
    });

    this.form.controls['periodFromStartMonth'].valueChanges.subscribe(value => {
      this._periodFromStartMonthChanging(value);
    });

    this.form.controls['dueDateMonthOffset'].valueChanges.subscribe(value => {
      this._dueDateOffsetChanging(value, this.form.controls['periodFromStartMonth'].value);
    });

    this._fillPeriodsFromArray(this.dataContext.invoicePeriod);
    this._filterDueDateMonth(this.form.controls['periodFromStartMonth'].value);
    this._fillDueDateDayArray(this.form.controls['yearlyDueDateMonth'].value);

    // ContractFormComponentBase.onRentalObjectChanged fills in propertyId and company by mutating the
    // shared dataContext, so this component never gets an ngOnChanges for it. Picking the rental object
    // after the payer would otherwise leave the direct debit match stuck on the no-property result.
    // The base class subscribes to this control first, so propertyId is already updated when we run.
    if (this.form.contains('rentalObject')) {
      this._subscription.add(this.form.controls['rentalObject'].valueChanges.subscribe(() => this._onRentalObjectChanged()));
    }
  }

  private _applyDirectDebitControls(invoicePayment: boolean): void {
    if (!invoicePayment) {
      this.form.removeControl('cancelDirectDebit');
      this.form.removeControl('createDirectDebit');
      this.form.removeControl('resetDirectDebitRequestDate');
      return;
    }

    // Re-applying after a direct debit re-match has to drop the controls the previous status added.
    this.form.removeControl('cancelDirectDebit');
    this.form.removeControl('createDirectDebit');

    const directDebitStatus = this.dataContext.directDebitStatus;
    if (directDebitStatus === DirectDebitStatus.Confirmed) {
      this.form.addControl('cancelDirectDebit', this._fcf.create('LEASE_AGREEMENT.CANCEL_DIRECT_DEBIT', false));
    } else if (directDebitStatus === DirectDebitStatus.Terminated) {
      this.form.addControl('createDirectDebit', this._fcf.create('LEASE_AGREEMENT.CREATE_DIRECT_DEBIT', false));
    } else if (directDebitStatus === undefined) {
      this.form.addControl('createDirectDebit', this._fcf.create('LEASE_AGREEMENT.CREATE_DIRECT_DEBIT', false));
    }

    if (this.dataContext.showResetDirectDebitRequestDate) {
      this.form.addControl(
        'resetDirectDebitRequestDate',
        this._fcf.create(
          'LEASE_AGREEMENT.REQUEST_DIRECT_DEBIT_AGAIN',
          false,
          null,
          null,
          'LEASE_AGREEMENT.HELP_TEXT.REQUEST_DIRECT_DEBIT_AGAIN'
        )
      );
    } else {
      this.form.removeControl('resetDirectDebitRequestDate');
    }
  }

  private _onRentalObjectChanged(): void {
    this._loadExternalAccountsReceivableSetting();

    if (!this.dataContext.propertyId) {
      return;
    }

    const selectedPayer = this.form.controls['payer']?.value;
    const payerId =
      typeof selectedPayer === 'string' && selectedPayer
        ? selectedPayer
        : typeof this.dataContext.payer === 'string'
          ? this.dataContext.payer
          : null;
    if (!payerId) {
      return;
    }

    if (this._payerSelectSub) {
      this._payerSelectSub.unsubscribe();
      this._payerSelectSub = null;
    }

    this._payerSelectSub = this._matchDirectDebit(payerId).subscribe(() => {
      this._payerSelectSub = null;
      // The status just changed, so the direct debit actions offered for it have to be rebuilt.
      if (this.externalAccountsReceivable !== true) {
        this._applyDirectDebitControls(this.form.controls['invoicePayment'].value);
      }
    });
  }

  private _loadExternalAccountsReceivableSetting(companyId = this.dataContext.company || this.company): void {
    if (this._externalAccountsReceivableSettingsSub) {
      this._externalAccountsReceivableSettingsSub.unsubscribe();
      this._externalAccountsReceivableSettingsSub = null;
    }

    // Until the current company's setting has been loaded, neither the ordinary nor the
    // external invoicing controls should be presented as authoritative.
    this.externalAccountsReceivable = null;

    if (!companyId) {
      return;
    }

    this._externalAccountsReceivableSettingsSub = this._companyService.getInvoiceDeliveryMethods(companyId).subscribe({
      next: deliveryMethods => {
        this._externalAccountsReceivableSettingsSub = null;
        this._applyExternalAccountsReceivableSetting(deliveryMethods.externalAccountsReceivable === true);
      },
      error: error => {
        this._externalAccountsReceivableSettingsSub = null;
        this._messageService.showError(error);
      },
    });
  }

  private _applyExternalAccountsReceivableSetting(enabled: boolean): void {
    this.externalAccountsReceivable = enabled;

    if (!enabled) {
      this._applyDirectDebitControls(this.form.controls['invoicePayment'].value);
      return;
    }

    // The backend permits an existing direct-debit agreement to remain unchanged, but no
    // new request or cancellation may be submitted while external receivables is active.
    const preserveExistingDirectDebit = !!this.dataContext.id && this.dataContext.invoicePayment === 1;
    this.form.controls['invoicePayment'].setValue(preserveExistingDirectDebit, { emitEvent: false });
    this.form.removeControl('cancelDirectDebit');
    this.form.removeControl('createDirectDebit');
    this.form.removeControl('resetDirectDebitRequestDate');
  }

  /**
   * Looks up the payer and matches it against the property's bankgiro, then writes the resulting
   * direct debit status and payer number to the form. Emits the fetched payer information.
   */
  private _matchDirectDebit(payerId: string): Observable<Payer> {
    // propertyId is only set once a rental object has been picked (see onRentalObjectChanged), which can
    // happen after the invoicing tab. Without it there's no bankgiro to match direct debit against.
    const bankgiro$: Observable<string> = this.dataContext.propertyId
      ? this._propertyDataService.getBankgiro(this.dataContext.propertyId)
      : of(null);

    return forkJoin([bankgiro$, this._dataService.getPaymentInformationForId(payerId)]).pipe(
      map(([bankgiro, payerInfo]) => {
        const directDebit = bankgiro
          ? payerInfo.directDebit.find(x => x.company === this.dataContext.company && x.bankgiro == bankgiro)
          : null;

        if (directDebit) {
          this.dataContext.directDebitStatus = directDebit.directDebitStatus;
          this.form.controls['payerNumber'].setValue(directDebit.payerNumber);
        } else {
          this.dataContext.directDebitStatus = undefined;
          this.form.controls['payerNumber'].setValue(payerInfo.number.toString());
        }

        return payerInfo;
      })
    );
  }

  onPayerSelected(item: AutoCompleteSelectionItem) {
    if (this._payerSelectSub) {
      this._payerSelectSub.unsubscribe();
      this._payerSelectSub = null;
    }

    const payer = item?.data as Tenant;
    if (!payer) {
      this.form.controls['payerEmailInvoicing'].reset();
      return;
    }

    if (this.dataContext.payer !== payer.id) {
      this.dataContext.payer = payer.id;
      this._updateAddressFromTenant(payer);
    }

    this._payerSelectSub = this._matchDirectDebit(payer.id).subscribe(payerInfo => {
      this._payerSelectSub = null;

      this.form.controls['payerEmailInvoicing'].setValue(payerInfo.emailInvoicing);

      if (this.externalAccountsReceivable === true) {
        this._applyExternalAccountsReceivableSetting(true);
        return;
      }

      if (!this._firstChange) {
        this.form.controls['invoicePayment'].setValue(false);
      } else if (this._firstChange) {
        this.form.controls['invoicePayment'].setValue(this.dataContext.invoicePayment === 1);
        this._firstChange = false;
      }
    });
  }

  private _updateAddressFromTenant(tenant: Tenant): void {
    this.addressForm.controls['name'].setValue(tenant.name);
    if (
      this.category !== LeaseAgreementCategory.Dwelling &&
      this.category !== LeaseAgreementCategory.CooperativeHousing &&
      !this.contractSpecificInvoiceAddress
    ) {
      this.addressForm.controls['additionalName'].setValue(tenant.invoiceAddress.additionalName);
      this.addressForm.controls['city'].setValue(tenant.invoiceAddress.city);
      this.addressForm.controls['country'].setValue(tenant.invoiceAddress.country);
      this.addressForm.controls['postalCode'].setValue(tenant.invoiceAddress.postalCode);
      this.addressForm.controls['street'].setValue(tenant.invoiceAddress.street);
    }
  }

  private _updating = false;

  private _invoicePeriodChanging(newValue: number) {
    if (this._updating) return;
    this._updating = true;
    this.form.controls['periodFromStartMonth'].setValue(PeriodStartMonth.January);
    this.form.controls['yearlyDueDateDay'].setValue(31);
    this.form.controls['dueDateMonthOffset'].setValue(-1);
    this.form.controls['yearlyDueDateMonth'].setValue(12);

    this._fillPeriodsFromArray(newValue);
    this._updating = false;

    this._periodFromStartMonthChanging(PeriodStartMonth.January);
  }

  private _periodFromStartMonthChanging(newValue: number) {
    if (this._updating) return;
    this._updating = true;
    this._filterDueDateMonth(newValue);
    this._updating = false;

    this._dueDateOffsetChanging(this.form.controls['dueDateMonthOffset'].value, newValue);
  }

  private _dueDateOffsetChanging(newValue: number, periodStartMonth: number) {
    if (this._updating) return;
    this._updating = true;
    const yearlyDueMonth = this._calcYearlyDueDateMonth(periodStartMonth, newValue);
    this.form.controls['yearlyDueDateMonth'].setValue(yearlyDueMonth);
    this._updating = false;

    this._refreshDays(yearlyDueMonth);
  }

  private _refreshDays(yearlyDueMonth: number) {
    let selectedDay = this.form.controls['yearlyDueDateDay'].value;
    if (!selectedDay) selectedDay = 31;
    selectedDay = Math.min(selectedDay, LocalDate.daysInMonth(2001, yearlyDueMonth));
    this.form.controls['yearlyDueDateDay'].setValue(selectedDay);
    this._fillDueDateDayArray(yearlyDueMonth);
  }

  private _calcYearlyDueDateMonth(periodFromStartMonth: number, dueMonthOffset: number): number {
    let result = periodFromStartMonth + dueMonthOffset;

    while (result < 1) result += 12;

    while (result > 12) result -= 12;

    return result;
  }

  private _getStartMonthFromDbValues(): number {
    let result = this.dataContext.yearlyDueDateMonth - this.dataContext.dueDateMonthOffset;

    while (result < 1) result += 12;

    while (result > 12) result -= 12;

    return result;
  }

  private _fillPeriodsFromArray(invoicePeriod: InvoicePeriod) {
    const periods = [];
    for (let month = 1; month <= invoicePeriod; month++) {
      let caption = PeriodToString(this._languageService.getCurrentTranslations(), month, invoicePeriod);
      for (let periodCount = 1; periodCount < 4; periodCount++) {
        if (periodCount * invoicePeriod + month <= 12) {
          if (periodCount == 3 && invoicePeriod != 3) {
            caption += ', ...';
          } else {
            caption +=
              ', ' +
              PeriodToString(
                this._languageService.getCurrentTranslations(),
                periodCount * invoicePeriod + month,
                invoicePeriod,
                true
              );
          }
        }
      }
      periods.push({ name: caption, value: month });
    }
    this.periodsFrom = periods;
  }

  private _filterDueDateMonth(startMonth: PeriodStartMonth) {
    if (this.invoicePeriodYear) this.dueDateMonths = this._getDueDates(startMonth, InvoicePeriod.Year, 12, 12);
    if (this.invoicePeriodHalfYear) this.dueDateMonths = this._getDueDates(startMonth, InvoicePeriod.HalfYear, 6, 6);
    if (this.invoicePeriodTertrial) this.dueDateMonths = this._getDueDates(startMonth, InvoicePeriod.Tertrial, 4, 4);
    if (this.invoicePeriodQuarter) this.dueDateMonths = this._getDueDates(startMonth, InvoicePeriod.Quarter, 3, 3);
    if (this.invoicePeriodTwoMonths) this.dueDateMonths = this._getDueDates(startMonth, InvoicePeriod.TwoMonths, 2, 2);
    if (this.invoicePeriodMonth) this.dueDateMonths = this._getDueDates(startMonth, InvoicePeriod.Month, 1, 1);
  }

  private _getMonthDescription(periodLength: number, month: number, advance: boolean, arrears: boolean) {
    if (periodLength === InvoicePeriod.Month) {
      if (month === 12) return 'DUE_PERIODS.ADVANCE';
      if (month === 1) return 'DUE_PERIODS.IN_THE_PERIOD';
      if (month === 2) return 'DUE_PERIODS.ARREARS';
    }

    const name = 'DUE_PERIODS.' + getMonthName(month).substr(6);
    if (advance) return name + '_ADVANCE';
    if (arrears) return name + '_ARREARS';
    return name + '_IN_PERIOD';
  }

  private _getDueDates(
    periodStart: PeriodStartMonth,
    periodLength: number,
    monthsBefore: number,
    monthsAfter: number
  ): { name: string; value: number }[] {
    const months = [];

    for (let offset = -monthsBefore; offset < periodLength + monthsAfter; offset++) {
      let monthInYear = periodStart + offset;

      while (monthInYear < 1) monthInYear += 12;

      while (monthInYear > 12) monthInYear -= 12;

      months.push({ name: this._getMonthDescription(periodLength, monthInYear, offset < 0, offset >= periodLength), value: offset });
    }

    return months;
  }

  private _fillDueDateDayArray(dueDateMonth: number) {
    const nonLeapYear = 2001;
    const daysInMonth = LocalDate.daysInMonth(nonLeapYear, dueDateMonth);
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) days.push({ name: day.toString(), value: day });

    this.yearlyDueDateDays = days;
  }

  onOpenRentPeriodsClicked(): void {
    if (this.dataContext.status === LeaseAgreementStatus.Draft) {
      this._messageService.showMessage('LEASE_AGREEMENT.INFORMATION_TEXT.DUE_DATES');
      return;
    }
    this._navigation.navigateChild([this.dataContext.id, 'rent-periods']);
  }
}
